import {
    createError,
    RelodeApiError,
    useNotifications,
    useRelodeFormik,
} from "@relode/core";
import { JobSalaryType } from "@relode/core/domain";

import { AddJobStep1Details } from "../../../../src/shared/types/addJobStep1Details";
import { AddJobStep2Details } from "../../../../src/shared/types/addJobStep2Details";
import { converters } from "../../../shared/backend";
import { jobs, teams } from "../../../shared/backend/controllers";
import { AddJobSeedData, AddJobSteps, AddJobStepsEnum } from "../types";
import { AddJobActionTypes, creators } from "./addJobActionTypes";

export const useAddJobActions = (
    dispatch: React.Dispatch<AddJobActionTypes>
) => {
    const { formik__infra, setFieldValue } = useRelodeFormik<AddJobSteps>();
    const { actions: notifications } = useNotifications();

    const uploadAttachment = async (attachment: File) => {
        try {
            dispatch(creators.attachmentStartUpload());
            const result = await jobs.jobService.uploadAttachment(attachment);

            formik__infra.setStatus({
                hasAttachmentUploadError: false,
            });

            setFieldValue(result, "stepOne", "attachment");

            dispatch(creators.attachmentUploadSucceeded());
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.attachmentUploadFailed(err?.summary));
                    return;
                }
            } else if (err instanceof TypeError) {
                dispatch(
                    creators.attachmentUploadFailed(
                        "there was a problem uploading your attachment, please contact support if this continues"
                    )
                );
                return;
            }
            throw createError(
                err,
                "client.addJob.addJobActions.uploadAttachment"
            );
        }
    };

    const loadAddJobNewStep1 = async (
        jobId: number,
        seedData?: AddJobSeedData
    ) => {
        try {
            dispatch(creators.loadAddJobStep1());

            const jobAddStep1Load = await jobs.jobService.getAddJobNewStep1(
                jobId
            );

            const teamsLoad = await teams.teamService.getTeamSuggest({
                searchText: "",
            });

            const [step1DetailsResponse, teamsResponse] = await Promise.all([
                jobAddStep1Load,
                teamsLoad,
            ]);

            const currentTeam = teamsResponse.find(
                x => x.isCurrentTeam == true
            );

            const step1Details = converters.convertAddJobStep1DetailsResponse(
                step1DetailsResponse ? step1DetailsResponse : null,
                currentTeam.hasActiveTeamPlan
            );

            let showOrderDetails;

            if (!!step1DetailsResponse) {
                setFieldValue(step1Details, "stepOne");
                showOrderDetails = step1Details.hasActiveTeamPlan;
            } else {
                const initial = AddJobSteps.create();
                initial.stepOne.teamId = currentTeam.teamId;
                initial.stepOne.hasActiveTeamPlan =
                    currentTeam.hasActiveTeamPlan;
                showOrderDetails = currentTeam.hasActiveTeamPlan;
                if (!!seedData) {
                    initial.stepTwo.jobName = seedData.jobName;
                    initial.stepTwo.jobTypeId = seedData.jobType.jobTypeId;
                    initial.stepTwo.jobSalaryInUSDollars = seedData.salary;
                    initial.stepTwo.jobSalaryTypeId = seedData.jobType.isFullTimeOrPartTime()
                        ? JobSalaryType.PerYear.id
                        : JobSalaryType.PerHour.id;
                }
                formik__infra.resetForm({ values: initial });
            }

            dispatch(
                creators.loadAddJobStep1Success(
                    teamsResponse,
                    jobId,
                    showOrderDetails
                )
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadAddJobStep1Failure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.addJob.addJobActions.loadAddJobStep1"
            );
        }
    };

    const saveAddJobNewStep1 = async (
        jobId: number,
        step1Details: AddJobStep1Details,
        showSnack = true
    ) => {
        dispatch(creators.saveAddJobStep1());
        try {
            const request = converters.convertAddJobStep1DetailsRequest(
                step1Details
            );
            const response = await jobs.jobService.saveAddJobNewStep1(
                jobId,
                request
            );

            const showOrderDetails = step1Details.hasActiveTeamPlan;

            dispatch(
                creators.saveAddJobStep1Success(
                    response.jobId,
                    showOrderDetails,
                    "job saved in pending status",
                    showSnack
                )
            );

            return response.jobId;
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.saveAddJobStep1Failure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.addJob.addJobActions.saveAddJobStep1"
            );
        }
    };

    const loadAddJobStep2 = async (jobId: number) => {
        try {
            dispatch(creators.loadAddJobStep2());

            const step2DetailsResponse = await jobs.jobService.getAddJobStep2(
                jobId
            );

            const step2Details = converters.convertAddJobStep2DetailsResponse(
                step2DetailsResponse
            );

            if (!!step2Details) {
                setFieldValue(step2Details, "stepTwo");
            }

            dispatch(creators.loadAddJobStep2Success());
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadAddJobStep2Failure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.addJob.addJobActions.loadAddJobStep2"
            );
        }
    };

    const saveAddJobStep2 = async (
        jobId: number,
        step2Details: AddJobStep2Details,
        showSnack = true
    ) => {
        dispatch(creators.saveAddJobStep2());
        try {
            const request = converters.convertAddJobStep2DetailsRequest(
                step2Details
            );
            await jobs.jobService.saveAddJobStep2(jobId, request);

            dispatch(
                creators.saveAddJobStep2Success(
                    "job saved in pending status",
                    showSnack
                )
            );
            return jobId;
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.saveAddJobStep2Failure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.addJob.addJobActions.saveAddJobStep2"
            );
        }
    };

    const loadAddJobStep3 = async (jobId: number) => {
        try {
            dispatch(creators.loadAddJobStep3());

            const step3DetailsResponse = await jobs.jobService.getAddJobStep3(
                jobId
            );

            const step3Details = converters.convertAddJobStep3DetailsResponse(
                step3DetailsResponse
            );

            if (!!step3Details) {
                setFieldValue(step3Details, "stepThree");
            }

            dispatch(creators.loadAddJobStep3Success());
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadAddJobStep3Failure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.addJob.addJobActions.loadAddJobStep3"
            );
        }
    };

    const moveJobWithoutPlanToRequested = async (
        jobId: number,
        step2Details: AddJobStep2Details
    ) => {
        dispatch(creators.moveJobToRequested());
        try {
            const request = converters.convertAddJobStep2DetailsRequest(
                step2Details
            );
            await jobs.jobService.moveJobWithoutPlanToRequested(jobId, request);

            const jobManagerDetailsResponse = await jobs.jobService.getJobManagerDetails(
                jobId
            );

            const jobManagerContact = converters.convertJobManagerContactResponse(
                jobManagerDetailsResponse
            );

            dispatch(
                creators.moveJobToRequestedSuccess(
                    "new job moved to requested status",
                    jobManagerContact
                )
            );
            return jobId;
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.moveJobToRequestedFailure(err));
                }
                return;
            }
            throw createError(
                err,
                "client.addJob.addJobActions.moveJobToRequested"
            );
        }
    };

    const moveJobWithPlanToRequested = async (
        jobId: number,
        jobConcurrencyToken: string
    ) => {
        dispatch(creators.moveJobToRequested());
        try {
            await jobs.jobService.moveJobWithPlanToRequested(
                jobId,
                jobConcurrencyToken
            );

            const jobManagerDetailsResponse = await jobs.jobService.getJobManagerDetails(
                jobId
            );

            const jobManagerContact = converters.convertJobManagerContactResponse(
                jobManagerDetailsResponse
            );

            dispatch(
                creators.moveJobToRequestedSuccess(
                    "new job moved to requested status",
                    jobManagerContact
                )
            );
            return jobId;
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.moveJobToRequestedFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.addJob.addJobActions.moveJobToRequested"
            );
        }
    };

    const copyJob = async (jobId: number) => {
        try {
            dispatch(creators.copyJob());

            const copyJobResponse = await jobs.jobService.copyJob(jobId);

            await loadAddJobNewStep1(copyJobResponse.jobId);

            dispatch(creators.copyJobSuccess(copyJobResponse.jobId));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.copyJobFailure(err));
                    return;
                }
            }
            throw createError(err, "client.addJob.addJobActions.copyJob");
        }
    };

    const saveStep = async (
        activeStep: string,
        jobId: number,
        jobSteps: AddJobSteps,
        showSnack: boolean
    ) => {
        switch (activeStep) {
            case AddJobStepsEnum.Step1: {
                formik__infra.setFieldError("stepOne", undefined);
                return await saveAddJobNewStep1(
                    jobId ? jobId : 0,
                    jobSteps.stepOne,
                    showSnack
                );
            }
            case AddJobStepsEnum.Step2: {
                formik__infra.setFieldError("stepTwo", undefined);
                return await saveAddJobStep2(
                    jobId,
                    jobSteps.stepTwo,
                    showSnack
                );
            }
            // this step is purely informational. no need to save or clear field errors
            case AddJobStepsEnum.Step3: {
                return jobId;
            }
        }
    };
    const handleNextStep = async (
        activeStep: string,
        jobId: number,
        jobSteps: AddJobSteps
    ) => {
        try {
            notifications.closeAllAreaNotifications();

            const isSuccessful = await saveStep(
                activeStep,
                jobId,
                jobSteps,
                false
            );

            if (isSuccessful) dispatch(creators.handleNextStep());
        } catch (err) {
            //no need to handle abort or client errors here since they are already handled inside each save function
            throw createError(
                err,
                "client.addJob.addJobActions.handleNextStep"
            );
        }
    };
    const handlePreviousStep = async (
        activeStep: string,
        jobId: number,
        jobSteps: AddJobSteps,
        hasChanges: boolean
    ) => {
        try {
            if (hasChanges) {
                await saveStep(activeStep, jobId, jobSteps, false);
            }
            dispatch(creators.handlePreviousStep());
        } catch (err) {
            //no need to handle abort or client errors here since they are already handled inside each save function
            throw createError(
                err,
                "client.addJob.addJobActions.handlePreviousStep"
            );
        }
    };

    const handleSaveAndExit = async (
        activeStep: string,
        jobId: number,
        jobSteps: AddJobSteps
    ) => {
        try {
            notifications.closeAllAreaNotifications();
            const _jobId = await saveStep(activeStep, jobId, jobSteps, true);

            if (_jobId) {
                dispatch(creators.exit(_jobId));
            }
        } catch (err) {
            //no need to handle abort or client errors here since they are already handled inside each save function
            throw createError(
                err,
                "client.addJob.addJobActions.handleSaveAndExit"
            );
        }
    };

    // UI
    const clearError = () => {
        dispatch(creators.clearError());
    };

    return {
        uploadAttachment,
        clearError,
        copyJob,
        loadAddJobNewStep1,
        saveAddJobNewStep1,
        loadAddJobStep2,
        saveAddJobStep2,
        loadAddJobStep3,
        moveJobWithoutPlanToRequested,
        moveJobWithPlanToRequested,
        handleNextStep,
        handlePreviousStep,
        handleSaveAndExit,
    };
};

export type AddJobActions = ReturnType<typeof useAddJobActions>;
